import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { errorNotify } from "../../Components/ToastComponent/ToastNotification";
import SelectInput from "../../Components/InputComponents/SelectInput";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  TextField,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  Grid,
} from "@mui/material";
import {
  Edit,
  Visibility,
  FilterList,
  Add,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { encryptId } from "../../Components/CryptoComponent";
import CommonButton from "../../Components/InputComponents/CommonButton";
import TextInput from "../../Components/InputComponents/TextInput";
import { useStaffLayout } from "../../Components/Context/CandidateLayoutContext";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const ViewClients = ({ isAdmin }) => {
  const { privilegeData } = useStaffLayout();
  function hasClientAddprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isClient = subModule.subModuleName.toLowerCase().includes("client");
      if (!isClient) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "add" && priv.assigned === true
      );
    });
  }

  function hasClientEditprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isClient = subModule.subModuleName.toLowerCase().includes("client");
      if (!isClient) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "edit" && priv.assigned === true
      );
    });
  }

  function hasClientViewprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isClient = subModule.subModuleName.toLowerCase().includes("client");
      if (!isClient) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "view" && priv.assigned === true
      );
    });
  }

  const navigate = useNavigate();
  const location = useLocation();

  const [clientData, setClientData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pageCount, setPageCount] = useState("");
  const [loaderOpen, setLoaderOpen] = useState(true);

  const [countryList, setCountryList] = useState([]);
  const [companyTypeList, setCompanyTypeList] = useState([]);

  const [open, setOpen] = useState(false);

  const [filterData, setFilterData] = useState({
    name: "",
    email: "",
    phone: "",
    companyType: "",
    country: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    status: "",
  });

  useEffect(() => {
    if (open != true) {
      setFilterData({
        ...filterData,
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        status: "",
      });
    }
  }, [open]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilters = () => {
    getData();
    setPage(0);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      name: "",
      phone: "",
      email: "",
      country: "",
      companyType: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      status: "",
    };

    setFilterData(resetFilters);
    getData(resetFilters);
    navigate("/Clients", { replace: true }); // removes the old route state
  };

  const paginationState = {
    pagination: { page, rowsPerPage },
    filterData: filterData,
    open: open,
  };

  const navigatetoAdd = () =>
    navigate(`/Clients/Add`, { state: paginationState });

  const navigatetoEdit = (id) => {
    const encrypted = encodeURIComponent(encryptId(id?.CD_Id));
    navigate(`/Clients/Edit/${encrypted}`, { state: paginationState });
  };

  const navigatetoView = (id) => {
    const encrypted = encodeURIComponent(encryptId(id?.CD_Id));
    navigate(`/Clients/View/${encrypted}`, { state: paginationState });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilterData({
      ...filterData,
      [name]: value,
    });
  };

  const getData = async (filters = filterData) => {
    try {
      const response = await axiosAPI?.post(
        `/Client/${rowsPerPage}/${page + 1}/listNew`,
        {
          name: filters?.name,
          email: filters?.email,
          phone: filters?.phone,
          country: filters?.country,
          company_type: filters?.companyType,
          contact_person: filters?.contactName,
          contact_person_email: filters?.contactEmail,
          contact_person_phone: filters?.contactPhone,
          status: filters?.status,
        }
      );

      const resData = response?.data?.data;
      setPageCount(resData?.total_count);
      setClientData(resData?.data);
    } catch (err) {
      errorNotify("Something went wrong");
      console.error(err);
    } finally {
      setLoaderOpen(false);
    }
  };

  const getCountries = async () => {
    try {
      const res = await axiosAPI.get(`/RecruitmentCountry/listCountry`);
      setCountryList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getCompanyType = async () => {
    try {
      const res = await axiosAPI.get(`/CompanyType/active/list`);
      setCompanyTypeList(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/CompanyType/list`);
        setCompanyTypeList(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getCountries();
    getCompanyType();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (location?.state?.pagination) {
      setPage(location.state.pagination.page || 0);
      setRowsPerPage(location.state.pagination.rowsPerPage || 25);
    }

    if (location?.state?.open) {
      setOpen(location?.state?.open);
    }

    if (location?.state?.filterData) {
      setFilterData(location.state.filterData);
      getData(location?.state?.filterData); // fetch with restored filters
    } else {
      getData();
    }
  }, [location?.state, page, rowsPerPage]);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        {/* Heading */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Client Management
        </Typography>

        {/* Add Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            ml: "auto",
          }}
        >
          {hasClientAddprivilege(privilegeData) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ background: "#E75E1F" }}
              onClick={navigatetoAdd}
              disabled={!hasClientAddprivilege(privilegeData)}
            >
              Add
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ maxWidth: "100%", mb: 2 }}>
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Name
            </Typography>
            <TextField
              placeholder="Name"
              variant="outlined"
              size="small"
              value={filterData?.name}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, name: e.target.value }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Email
            </Typography>
            <TextField
              placeholder="Email"
              variant="outlined"
              size="small"
              value={filterData?.email}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, email: e.target.value }))
              }
              fullWidth
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Phone
            </Typography>
            <TextField
              placeholder="Phone"
              variant="outlined"
              size="small"
              value={filterData?.phone}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, phone: e.target.value }))
              }
              fullWidth
            />
          </Grid>

          {/* Company Type */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Company Type
            </Typography>
            <SelectInput
              value={filterData?.companyType}
              name="companyType"
              onChange={handleFilterChange}
              options={companyTypeList?.map((item) => ({
                value: item.CT_Id,
                label: item.CT_Name,
              }))}
              placeholder="Select Company Type"
            />
          </Grid>

          {/* Country */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Country
            </Typography>
            <SelectInput
              value={filterData?.country}
              name="country"
              onChange={handleFilterChange}
              options={countryList?.map((item) => ({
                value: item.CL_ID,
                label: item.CL_Name,
              }))}
              placeholder="Select Country"
            />
          </Grid>

          {open && (
            <>
              {/* Contact Person Name */}
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Contact Person Name
                </Typography>
                <TextField
                  label="Contact Person Name"
                  placeholder="Contact Person Name"
                  variant="outlined"
                  size="small"
                  sx={{ width: { xs: "100%" } }}
                  value={filterData?.contactName}
                  onChange={(e) =>
                    setFilterData((prev) => ({
                      ...prev,
                      contactName: e.target.value,
                    }))
                  }
                />
              </Grid>

              {/* Contact Person Email */}
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Contact Person Email
                </Typography>

                <TextField
                  placeholder="Contact Person Email"
                  variant="outlined"
                  size="small"
                  sx={{ width: { xs: "100%" } }}
                  value={filterData?.contactEmail}
                  onChange={(e) =>
                    setFilterData((prev) => ({
                      ...prev,
                      contactEmail: e.target.value,
                    }))
                  }
                />
              </Grid>

              {/* Contact Person Phone */}
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Contact Person Phone
                </Typography>

                <TextField
                  placeholder="Contact Person Phone"
                  variant="outlined"
                  size="small"
                  sx={{ width: { xs: "100%" } }}
                  value={filterData?.contactPhone}
                  onChange={(e) =>
                    setFilterData((prev) => ({
                      ...prev,
                      contactPhone: e.target.value,
                    }))
                  }
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Status
                </Typography>
                <SelectInput
                  value={filterData?.status}
                  name="status"
                  onChange={handleFilterChange}
                  options={[
                    { value: 1, label: "Active" },
                    { value: 2, label: "Inactive" },
                  ]}
                  placeholder="Select Status"
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Search/Reset/Filter BTN */}
      <Box
        sx={{
          width: "100%", // Full width
          display: "flex",
          justifyContent: "flex-end", // Align buttons to the right
          alignItems: "flex-end",
          gap: 1,
          my: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Search />}
            onClick={handleApplyFilters}
            sx={{
              textTransform: "capitalize",
              color: "white",
              background: "#006CB8",
            }}
          >
            Search
          </Button>

          <Button
            variant="outlined"
            startIcon={<RestartAlt />}
            onClick={handleResetFilters}
            sx={{ textTransform: "capitalize" }}
          >
            Reset
          </Button>

          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setOpen(!open)}
            sx={{ textTransform: "capitalize" }}
          >
            {open ? "Less" : "More"} Filters
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid lightgray",
          borderRadius: "10px",
          maxWidth: "100vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Phone</StyledTableCell>
              <StyledTableCell>Country</StyledTableCell>
              <StyledTableCell align="center">Status</StyledTableCell>
              {(hasClientEditprivilege(privilegeData) ||
                hasClientViewprivilege(privilegeData)) && (
                  <StyledTableCell align="center" sx={{ width: "150px" }}>
                    {" "}
                    Actions{" "}
                  </StyledTableCell>
                )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loaderOpen ? (
              Array.from({ length: 7 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: 7 }).map((_, index) => (
                    <StyledTableCell key={index}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={40}
                      />
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))
            ) : clientData && clientData?.length > 0 ? (
              clientData.map((parent, index) => (
                <StyledTableRow key={parent.id}>
                  <StyledTableCell>
                    {" "}
                    {index + 1 + page * rowsPerPage}{" "}
                  </StyledTableCell>
                  <StyledTableCell sx={{ cursor:'pointer'}} onClick={() => {navigatetoView(parent);}}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: "12px", sm: "14px" },
                          fontFamily: "Geist, sans-serif",

                        }}

                      >
                        {parent?.CD_Name}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    {" "}
                    {parent?.CD_Email ? parent?.CD_Email : "-"}{" "}
                  </StyledTableCell>
                  <StyledTableCell>
                    {" "}
                    {parent?.CD_Phone ? parent?.CD_Phone : "-"}{" "}
                  </StyledTableCell>
                  <StyledTableCell> {parent?.CD_CountryName} </StyledTableCell>
                  <StyledTableCell align="center">
                    <Chip
                      label={parent?.CD_StatusName}
                      color={
                        parent?.CD_StatusName === "Active" ? "success" : "error"
                      }
                      sx={{
                        fontSize: { xs: "10px", sm: "12px" },
                        width: "80px",
                        fontFamily: "Geist, sans-serif",
                        fontWeight: "600",
                      }}
                    />
                  </StyledTableCell>
                  {(hasClientViewprivilege(privilegeData) ||
                    hasClientEditprivilege(privilegeData)) && (
                      <StyledTableCell align="center">
                        {hasClientViewprivilege(privilegeData) && (
                          <IconButton
                            color="primary"
                            disabled={!hasClientViewprivilege(privilegeData)}
                            onClick={() => {
                              navigatetoView(parent);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        )}

                        {hasClientEditprivilege(privilegeData) && (
                          <IconButton
                            color="warning"
                            disabled={!hasClientEditprivilege(privilegeData)}
                            onClick={() => {
                              navigatetoEdit(parent);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </StyledTableCell>
                    )}
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={9} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: "20px",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={pageCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default ViewClients;
